import {
	type AdminCharacter,
	type AdminEpisodeDetail,
	type AdminGoldenAsset,
	type AdminLocation,
	GoldenAssetKind,
	GoldenAssetStatus,
} from "@storyline/shared";

export type ReferenceRequirement = {
	id: string;
	name: string;
	approved: boolean;
};

export type RequiredReferences = {
	characters: ReferenceRequirement[];
	locations: ReferenceRequirement[];
	allApproved: boolean;
};

const isApproved = (asset: AdminGoldenAsset): boolean =>
	asset.status === GoldenAssetStatus.APPROVED || asset.status === GoldenAssetStatus.LOCKED;

export const requiredReferences = (
	episode: AdminEpisodeDetail,
	characters: AdminCharacter[],
	locations: AdminLocation[],
	assets: AdminGoldenAsset[],
): RequiredReferences => {
	const characterIds = new Set<string>();
	const locationIds = new Set<string>();
	for (const node of episode.nodes) {
		for (const shot of node.shots) {
			for (const characterId of shot.characterIds) {
				characterIds.add(characterId);
			}
			if (shot.locationId !== null) {
				locationIds.add(shot.locationId);
			}
		}
	}

	const approvedFace = new Set(
		assets.filter((asset) => asset.kind === GoldenAssetKind.CHARACTER_FACE && isApproved(asset)).map((asset) => asset.ownerId),
	);
	const approvedMaster = new Set(
		assets.filter((asset) => asset.kind === GoldenAssetKind.LOCATION_MASTER && isApproved(asset)).map((asset) => asset.ownerId),
	);

	const characterReqs = [...characterIds].map((id) => ({
		id,
		name: characters.find((character) => character.id === id)?.name ?? id,
		approved: approvedFace.has(id),
	}));
	const locationReqs = [...locationIds].map((id) => ({
		id,
		name: locations.find((location) => location.id === id)?.name ?? id,
		approved: approvedMaster.has(id),
	}));

	return {
		characters: characterReqs,
		locations: locationReqs,
		allApproved: [...characterReqs, ...locationReqs].every((requirement) => requirement.approved),
	};
};
